/**
 * Panel del hilo de seguimiento del formulario.
 *
 * El backend viene escribiendo auditoria sola desde el primer dia —cada
 * confirmacion, cada conciliacion, cada liquidacion deja un mensaje en el hilo
 * del registro— y hasta ahora **no habia forma de leerlo**. Se escribia un
 * diario invisible. Este panel es la ventana a ese diario, y lo que convierte
 * «el sistema hizo algo» en «se quien lo hizo, cuando y por que».
 *
 * Es generico como todo el renderer: no sabe de facturas, de expensas ni de
 * abonos. Se dibuja cuando la metadata del modelo dice `chatter: true`, bandera
 * que el backend DEDUCE de que el modelo herede `ChatterMixin` —no se declara
 * modelo por modelo— asi que sirve igual en los trece modulos.
 *
 * Cuatro decisiones que definen su comportamiento:
 *
 * 1. **Leer el hilo pide `read`; escribir en el pide `write`.** El servidor
 *    decide y lo contesta en `can_post`; sin eso no se dibujan ni el cuadro de
 *    texto ni el boton de agendar. Esconderlos es cortesia: el servidor lo
 *    vuelve a exigir en cada POST.
 * 2. **Los mensajes van del mas viejo al mas nuevo.** Se lee como un diario, no
 *    como un feed. El que abre una factura discutida quiere seguir la historia
 *    en orden, no reconstruirla al reves.
 * 3. **Los cambios se muestran con la etiqueta del formulario, no con el nombre
 *    del campo.** «Estado: Borrador -> Confirmada», no «state: draft -> open».
 *    La traduccion la hace el servidor, que es el que tiene la metadata.
 * 4. **Una nota nace siempre como nota interna.** El tipo no viaja desde el
 *    cliente: fabricar una linea de auditoria a mano convertiria el hilo en un
 *    documento editable, que es lo contrario de para lo que existe.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';

import { DatePickerInput } from '@/components/fields/DatePickerInput';
import { formatDate, formatDateTime, formatRelativeDate } from '@/components/fields/format';
import { IconBell, IconChat, IconCheck, IconTrash } from '@/components/shell/icons';
import { Badge, Button } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import { ApiError, api } from '@/lib/api';
import type { ChatterActivity, ChatterChange, ChatterMessage, ChatterThread } from '@/lib/types';
import { BotonBloqueado } from '@/views/MotivoBloqueo';
import { DATA_KEYS, useCompanyId } from '@/views/queries';

export interface ChatterPanelProps {
  /** Nombre logico del modelo (`invoicing.invoice`). */
  modelName: string;
  /** Id del registro. El panel no se monta con un registro sin guardar. */
  recordId: number;
}

/** El mensaje del servidor cuando lo hay; si no, nada. */
function mensajeDe(error: unknown): string | undefined {
  return error instanceof ApiError ? error.message : undefined;
}

/**
 * Iniciales del autor para el circulito. Dos letras como maximo: mas que eso ya
 * no se lee a 24 pixeles.
 */
function iniciales(nombre: string): string {
  const partes = nombre.trim().split(/\s+/).filter((p) => p !== '');
  if (partes.length === 0) return '·';
  const primera = partes[0]?.[0] ?? '';
  const segunda = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return (primera + segunda).toUpperCase();
}

/**
 * Un valor de `changes` como texto.
 *
 * Llega tal cual lo serializo el backend (`_to_json`): string, numero, booleano,
 * `null` o el `{id, display_name}` de un many2one. Un vacio se dibuja como «—»
 * porque «cambio de nada a algo» es informacion y no un renglon en blanco.
 */
function mostrarValor(valor: unknown): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (typeof valor === 'boolean') return valor ? 'Sí' : 'No';
  if (typeof valor === 'object') {
    const registro = valor as { display_name?: unknown };
    if (typeof registro.display_name === 'string') return registro.display_name;
    return JSON.stringify(valor);
  }
  return String(valor);
}

/**
 * El tono de la pastilla segun quien escribio.
 *
 * La auditoria va apagada —es el ruido de fondo, hay una por cada guardado— y
 * lo que escribio una persona va resaltado: en un hilo largo lo que se busca es
 * la nota que alguien dejo, no la enesima traza automatica.
 */
function tonoDeMensaje(kind: string): 'neutral' | 'accent' | 'success' {
  if (kind === 'audit') return 'neutral';
  if (kind === 'note') return 'accent';
  return 'success';
}

/** Una entrada del hilo. */
function EntradaDelHilo({ mensaje }: { mensaje: ChatterMessage }) {
  const autor = mensaje.author?.display_name ?? 'El sistema';
  const esAuditoria = mensaje.kind === 'audit';

  return (
    <li className="flex gap-3 py-3">
      <span
        aria-hidden="true"
        className={clsx(
          'mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full text-2xs font-semibold',
          esAuditoria ? 'bg-surface-raised text-fg-faint' : 'bg-accent-soft text-accent',
        )}
      >
        {mensaje.author === null ? '⚙' : iniciales(autor)}
      </span>

      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-fg-muted">
          <span className="font-medium text-fg">{autor}</span>
          {/* Relativa para ubicarse de un vistazo, exacta en el tooltip para el
              que tiene que declarar cuando paso. */}
          <span title={formatDateTime(mensaje.created_at)}>
            {formatRelativeDate(mensaje.created_at)}
            {mensaje.created_at !== null && ` · ${formatDateTime(mensaje.created_at)}`}
          </span>
          <Badge tone={tonoDeMensaje(mensaje.kind)}>{mensaje.kind_label}</Badge>
        </p>

        {mensaje.subject !== '' && (
          <p className="mt-1 text-sm font-semibold">{mensaje.subject}</p>
        )}
        {mensaje.body !== '' && (
          <p className="mt-0.5 whitespace-pre-wrap break-words text-sm">{mensaje.body}</p>
        )}

        {mensaje.changes.length > 0 && (
          <ul className="mt-1.5 space-y-0.5">
            {mensaje.changes.map((cambio: ChatterChange) => (
              <li key={cambio.field} className="text-xs text-fg-muted">
                <span className="font-medium text-fg">{cambio.label}:</span>{' '}
                <span className="line-through decoration-fg-faint">{mostrarValor(cambio.old)}</span>
                {' → '}
                <span className="font-medium text-fg">{mostrarValor(cambio.new)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

/** Un pendiente del registro, con su vencimiento y su responsable. */
function FilaDePendiente({
  actividad,
  puedeEscribir,
  onHecha,
  onBorrar,
  ocupada,
}: {
  actividad: ChatterActivity;
  puedeEscribir: boolean;
  onHecha: (a: ChatterActivity) => void;
  onBorrar: (a: ChatterActivity) => void;
  ocupada: boolean;
}) {
  const hecha = actividad.state === 'done';
  return (
    <li className="flex items-start gap-2.5 py-2">
      <span
        aria-hidden="true"
        className={clsx(
          'mt-1 h-2 w-2 shrink-0 rounded-full',
          hecha ? 'bg-fg-faint' : actividad.overdue ? 'bg-danger' : 'bg-accent',
        )}
      />
      <div className="min-w-0 flex-1">
        <p className={clsx('truncate text-sm font-medium', hecha && 'text-fg-muted line-through')}>
          {actividad.summary}
        </p>
        <p className="truncate text-xs text-fg-muted">
          {actividad.due_date === null
            ? 'Sin fecha'
            : `Vence ${formatDate(actividad.due_date)} (${formatRelativeDate(actividad.due_date)})`}
          {actividad.assigned_to !== null && ` · ${actividad.assigned_to.display_name}`}
          {actividad.activity_type !== '' && ` · ${actividad.activity_type}`}
        </p>
        {actividad.note !== '' && (
          <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-fg-muted">
            {actividad.note}
          </p>
        )}
      </div>
      {actividad.overdue && <Badge tone="danger">Vencida</Badge>}
      {puedeEscribir && !hecha && (
        <Button
          size="sm"
          variant="ghost"
          aria-label={`Marcar «${actividad.summary}» como hecha`}
          loading={ocupada}
          onClick={() => {
            onHecha(actividad);
          }}
        >
          <IconCheck className="h-4 w-4" />
          <span className="hidden sm:inline">Hecha</span>
        </Button>
      )}
      {puedeEscribir && (
        <Button
          size="sm"
          variant="ghost"
          aria-label={`Eliminar el pendiente «${actividad.summary}»`}
          onClick={() => {
            onBorrar(actividad);
          }}
        >
          <IconTrash className="h-4 w-4" />
        </Button>
      )}
    </li>
  );
}

export function ChatterPanel({ modelName, recordId }: ChatterPanelProps) {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();
  const [nota, setNota] = useState('');
  const [agendando, setAgendando] = useState(false);
  const [resumen, setResumen] = useState('');
  const [vence, setVence] = useState('');
  const [detalle, setDetalle] = useState('');
  const [enCurso, setEnCurso] = useState<number | null>(null);

  // La clave cuelga de la del modelo: confirmar, netear o liquidar invalidan
  // «los datos» y con ellos el hilo, que es donde el servidor acaba de escribir.
  const clave = DATA_KEYS.chatter(companyId, modelName, recordId);
  const consulta = useQuery({
    queryKey: clave,
    queryFn: ({ signal }) => api.fetchChatter(modelName, recordId, signal),
    // El hilo lo escribe el sistema en cada accion y lo escriben otras personas:
    // la cache caduca rapido, igual que la de los datos de negocio.
    staleTime: 15_000,
  });

  const datos: ChatterThread | undefined = consulta.data;
  const puedeEscribir = datos?.can_post === true;

  function refrescar(): void {
    void queryClient.invalidateQueries({ queryKey: clave });
  }

  const anotar = useMutation({
    mutationFn: () => api.postChatterNote(modelName, recordId, nota.trim()),
    onSuccess: () => {
      setNota('');
      notify.success('Nota agregada al hilo.');
      refrescar();
    },
    onError: (error: unknown) => {
      notify.error('No se pudo agregar la nota.', mensajeDe(error));
    },
  });

  const seguir = useMutation({
    mutationFn: (siguiendo: boolean) =>
      siguiendo ? api.unfollowRecord(modelName, recordId) : api.followRecord(modelName, recordId),
    onSuccess: (resultado) => {
      notify.success(resultado.following ? 'Ahora seguís este registro.' : 'Dejaste de seguirlo.');
      refrescar();
    },
    onError: (error: unknown) => {
      notify.error('No se pudo cambiar la suscripción.', mensajeDe(error));
    },
  });

  const agendar = useMutation({
    mutationFn: () =>
      api.scheduleActivity(modelName, recordId, {
        summary: resumen.trim(),
        ...(vence === '' ? {} : { due_date: vence }),
        ...(detalle.trim() === '' ? {} : { note: detalle.trim() }),
      }),
    onSuccess: () => {
      setResumen('');
      setVence('');
      setDetalle('');
      setAgendando(false);
      notify.success('Pendiente agendado.');
      refrescar();
    },
    onError: (error: unknown) => {
      notify.error('No se pudo agendar el pendiente.', mensajeDe(error));
    },
  });

  const moverActividad = useMutation({
    mutationFn: (actividad: ChatterActivity) =>
      api.setActivityState(modelName, recordId, actividad.id, 'done'),
    onSuccess: () => {
      setEnCurso(null);
      notify.success('Pendiente marcado como hecho.');
      refrescar();
    },
    onError: (error: unknown) => {
      setEnCurso(null);
      notify.error('No se pudo marcar el pendiente.', mensajeDe(error));
    },
  });

  const borrarActividad = useMutation({
    mutationFn: (actividad: ChatterActivity) =>
      api.deleteActivity(modelName, recordId, actividad.id),
    onSuccess: () => {
      notify.success('Pendiente eliminado.');
      refrescar();
    },
    onError: (error: unknown) => {
      notify.error('No se pudo eliminar el pendiente.', mensajeDe(error));
    },
  });

  // Igual que el panel de adjuntos: si el servidor contesto que no, no se
  // dibuja una pared de error donde iba un panel secundario.
  if (consulta.isError) return null;

  const mensajes = datos?.messages ?? [];
  // Las canceladas no se muestran: si alguien las cancelo es porque dejaron de
  // ser trabajo. Quedan en la base y el que las canceló ya lo sabe.
  const pendientes = (datos?.activities ?? []).filter((a) => a.state !== 'cancelled');
  const siguiendo = datos?.following === true;

  return (
    <section aria-labelledby="hilo-titulo" className="mt-6 rounded-xl border border-line bg-surface">
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3 sm:px-6">
        <IconChat className="h-4 w-4 text-fg-muted" />
        <h2 id="hilo-titulo" className="text-sm font-semibold">
          Seguimiento
        </h2>
        <span className="text-sm text-fg-muted">{datos?.message_count ?? 0}</span>
        {(datos?.followers.length ?? 0) > 0 && (
          <span
            className="text-xs text-fg-muted"
            title={(datos?.followers ?? [])
              .map((f) => f.user?.display_name ?? '—')
              .join(', ')}
          >
            · {datos?.followers.length} siguiendo
          </span>
        )}
        {puedeEscribir && (
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant={siguiendo ? 'secondary' : 'ghost'}
              loading={seguir.isPending}
              onClick={() => {
                seguir.mutate(siguiendo);
              }}
            >
              <IconBell className="h-4 w-4" />
              <span className="hidden sm:inline">{siguiendo ? 'Siguiendo' : 'Seguir'}</span>
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setAgendando((abierto) => !abierto);
              }}
            >
              Agendar pendiente
            </Button>
          </div>
        )}
      </header>

      {/* Pendientes primero: es lo que hay que hacer, y va antes que lo que ya pasó. */}
      {(pendientes.length > 0 || agendando) && (
        <div className="border-b border-line px-4 py-2 sm:px-6">
          <h3 className="pt-1 text-2xs font-semibold uppercase tracking-wide text-fg-faint">
            Pendientes
          </h3>
          {pendientes.length > 0 && (
            <ul className="divide-y divide-line">
              {pendientes.map((actividad) => (
                <FilaDePendiente
                  key={actividad.id}
                  actividad={actividad}
                  puedeEscribir={puedeEscribir}
                  ocupada={enCurso === actividad.id}
                  onHecha={(a) => {
                    setEnCurso(a.id);
                    moverActividad.mutate(a);
                  }}
                  onBorrar={(a) => {
                    borrarActividad.mutate(a);
                  }}
                />
              ))}
            </ul>
          )}

          {agendando && puedeEscribir && (
            <div className="my-2 space-y-2 rounded-lg border border-line bg-surface-raised p-3">
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  value={resumen}
                  autoFocus
                  placeholder="Qué hay que hacer (llamar, revisar, firmar…)"
                  aria-label="Resumen del pendiente"
                  onChange={(evento) => {
                    setResumen(evento.target.value);
                  }}
                  className="h-9 flex-1 rounded-md border border-line bg-surface px-3 text-sm outline-none focus:border-accent"
                />
                {/* El control propio y no un `<input type="date">`: el nativo
                    dibuja la fecha segun el locale del sistema operativo y en una
                    maquina en ingles sale `mm/dd/aaaa`. Es el mismo motivo por el
                    que existe `DatePickerInput` en el resto del formulario. */}
                <div className="sm:w-44">
                  {/* La etiqueta va oculta y no como `aria-label` porque el
                      control es de terceros dentro de la app —lo dibuja
                      `DatePickerInput`— y lo único que expone es el `id`. Sin
                      nombre accesible el campo se lee «edit text, dd/mm/aaaa». */}
                  <label htmlFor="pendiente-vence" className="sr-only">
                    Fecha de vencimiento
                  </label>
                  <DatePickerInput
                    id="pendiente-vence"
                    value={vence}
                    calendarLabel="Elegir la fecha del pendiente"
                    onChange={(iso) => {
                      setVence(iso ?? '');
                    }}
                  />
                </div>
              </div>
              <input
                value={detalle}
                placeholder="Detalle (opcional)"
                aria-label="Detalle del pendiente"
                onChange={(evento) => {
                  setDetalle(evento.target.value);
                }}
                className="h-9 w-full rounded-md border border-line bg-surface px-3 text-sm outline-none focus:border-accent"
              />
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setAgendando(false);
                  }}
                >
                  Cancelar
                </Button>
                {resumen.trim() === '' ? (
                  <BotonBloqueado
                    size="sm"
                    variant="primary"
                    label="Agendar"
                    motivo="Falta escribir qué hay que hacer: el pendiente se guarda con ese texto y es lo que vas a leer cuando venza."
                  />
                ) : (
                  <Button
                    size="sm"
                    variant="primary"
                    loading={agendar.isPending}
                    onClick={() => {
                      agendar.mutate();
                    }}
                  >
                    Agendar
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="px-4 py-2 sm:px-6">
        {consulta.isPending ? (
          <p className="py-4 text-sm text-fg-muted">Cargando el hilo…</p>
        ) : mensajes.length === 0 ? (
          <p className="py-4 text-sm text-fg-muted">
            Todavía no pasó nada en este registro. Cada confirmación, cada cambio de estado y cada
            nota van a quedar acá.
          </p>
        ) : (
          <>
            {datos?.truncated === true && (
              <p className="pt-2 text-xs text-fg-faint">
                Se muestran las últimas {mensajes.length} de {datos.message_count} entradas.
              </p>
            )}
            <ul className="divide-y divide-line">
              {mensajes.map((mensaje) => (
                <EntradaDelHilo key={mensaje.id} mensaje={mensaje} />
              ))}
            </ul>
          </>
        )}

        {puedeEscribir && (
          <div className="border-t border-line py-3">
            <textarea
              value={nota}
              rows={2}
              placeholder="Escribí una nota interna sobre este registro…"
              aria-label="Nota interna"
              onChange={(evento) => {
                setNota(evento.target.value);
              }}
              className="w-full resize-y rounded-md border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
            />
            <div className="mt-2 flex items-center justify-between gap-2">
              <p className="text-xs text-fg-faint">
                Queda en la historia del registro, con tu nombre y la fecha. No se puede editar.
              </p>
              {nota.trim() === '' ? (
                <BotonBloqueado
                  size="sm"
                  variant="primary"
                  label="Anotar"
                  motivo="Falta escribir la nota en el recuadro de arriba."
                />
              ) : (
                <Button
                  size="sm"
                  variant="primary"
                  loading={anotar.isPending}
                  onClick={() => {
                    anotar.mutate();
                  }}
                >
                  Anotar
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
